// ===== 認証関連の型定義 =====

export interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  provider: 'google' | 'microsoft';
  createdAt: string;
  updatedAt: string;
}

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithMicrosoft: () => Promise<void>;
  /**
   * 別プロバイダで登録済みのメールアドレスに、今回のログイン手段を追加する。
   * signInWithGoogle / signInWithMicrosoft が AccountLinkRequiredError を投げたときに、
   * その error をそのまま渡して呼ぶ。既存の手段でのログインを1回挟むため、
   * ブラウザにポップアップを塞がれないようユーザー操作（ボタン押下）から呼ぶこと。
   */
  linkPendingCredential: (error: AccountLinkRequired) => Promise<void>;
  signOut: () => Promise<void>;
}

/** アカウント連携が必要なときに投げられるエラーの形。実体は AuthContext 側で定義する。 */
export interface AccountLinkRequired extends Error {
  code: 'auth/account-link-required';
  /** 登録済みのメールアドレス。メール列挙保護により取得できないことがある */
  email: string | null;
  /** 今回サインインに使った、まだ紐づいていない資格情報 */
  pendingCredential: unknown;
  /** 本人確認に使う既存のログイン手段 */
  existingProvider: 'google' | 'microsoft';
}

// ===== ファクトチェック関連の型定義 =====

export interface ExtractedFact {
  claim: string;
  /** 主張の根拠となる原文からの引用 */
  quote: string;
  /** @deprecated 旧フィールド名。保存済みデータの読み出し用に残している */
  evidence?: string;
  verification_strategy: 'internal_knowledge' | 'google_search';
  page: number;
  line_start: number;
  confidence: number;

  // 動画用フィールド（オプショナル）
  start_sec?: number; // シーク開始位置（秒）
  end_sec?: number; // シーク終了位置（秒）
  segment_ids?: string[]; // ASRセグメントID
}

export interface VerificationResult {
  claim: string;
  result: string;
  source: string;
  /** ファクト抽出時の原文引用 */
  quote: string;
  /** @deprecated 旧フィールド名。保存済みデータの読み出し用に残している */
  evidence?: string;
  confidence: number;
  method?: string;
  processing_time?: number;
}

export interface IndexedVerificationResult extends VerificationResult {
  originalIndex: number; // 元の配列での位置
  factId: string; // ファクトの一意識別子
  completedAt?: string; // 完了日時
}

// 検証結果の詳細な構造（source JSONをパースした後の型）
export interface VerificationSourceData {
  judgement: string;
  confidence: number;
  summary: string;
  reason: string;
  sources?: WebSource[]; // Google検索検証のみ
  thought_process?: string; // Google検索検証のみ
  grounding_metadata?: GroundingMetadata; // Google検索検証のみ
}

export interface WebSource {
  title: string;
  url: string;
}

export interface GroundingMetadata {
  web_search_queries: string[];
  grounding_chunks: GroundingChunk[];
  grounding_supports: GroundingSupport[];
}

export interface GroundingChunk {
  title: string;
  uri: string;
}

export interface GroundingSupport {
  segment: {
    start_index: number;
    end_index: number;
    text: string;
  };
  grounding_chunk_indices: number[];
}

export interface FactCheckProject {
  id: string;
  userId: string; // Firestore用のユーザーID
  title: string;
  description?: string;
  created_at: string;
  updated_at: string;
  status: 'extracting' | 'extracted' | 'verifying' | 'processing' | 'completed';
  pdf_filename: string;
  pdf_url: string;
  pdf_blob_name?: string; // GCS削除用のブロブ名
  total_facts: number;
  verified_facts: number;
  facts: ExtractedFact[];
  verification_results: VerificationResult[];
  selectedFactIndices?: number[]; // 選択状態の保存用
  group_id?: string;
  group_name?: string;
}

// ===== UI関連の型定義 =====

export interface DashboardStats {
  total_projects: number;
  completed_projects: number;
  total_facts_verified: number;
  average_accuracy: number;
}

export interface ItemFilter {
  status?: 'all' | FactCheckItemStatus;
  date_range?: {
    start: string;
    end: string;
  };
  search_query?: string;
}

// ===== API関連の型定義 =====

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface FactExtractionResponse {
  session_id: string;
  facts: ExtractedFact[];
  total_count: number;
  processing_time: number;
  pdf_url: string;
  pdf_blob_name: string; // GCS削除用
  // テキストPDFは "completed"（facts即取得）、スキャンPDFは "processing"（非同期OCR→ポーリング）
  status?: 'completed' | 'processing';
}

// スキャンPDFの非同期OCR処理ステータス（/api/document-status）
export interface DocumentStatusResponse {
  session_id: string;
  status: 'processing' | 'completed' | 'error' | null;
  stage: 'ocr' | 'extracting' | 'completed' | 'error' | null;
  progress: number;
  message: string | null;
  facts: ExtractedFact[];
  total_count: number;
  page_count: number | null;
  error: string | null;
  updated_at: string | null;
}

export interface FactVerificationResponse {
  results: VerificationResult[];
  total_processing_time: number;
  performance_stats: {
    total_facts: number;
    avg_processing_time: number;
  };
}

// ===== ローカルストレージ用の型定義 =====

export interface LocalStorageData {
  projects: Record<string, FactCheckProject>;
  settings: UserSettings;
  last_updated: string;
}

export interface UserSettings {
  theme: 'light' | 'dark';
  default_verification_strategy: 'internal_knowledge' | 'google_search';
  auto_save?: boolean;
}

// ===== 追加調査関連の型定義 =====

export interface InvestigationTarget {
  id: string;
  userId: string;
  project_id: string;
  project_title: string;
  fact_index: number;
  claim: string;
  verification_result: string;
  verification_summary: string; // source.summaryから抽出
  confidence: number;
  pdf_filename: string;
  pdf_url: string;
  added_at: string;
  investigation_count: number;
  last_investigated_at: string | null;
}

export interface InvestigationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  agent?: string;
  timestamp: string;
  updated_at?: string;
  metadata?: Record<string, any>;
  edited?: boolean;
  lastEdited?: string;
  fact_reference?: {
    project_id: string;
    project_title: string;
    fact_index: number;
    claim: string;
  };
}

export interface InvestigationSession {
  id: string;
  userId: string;
  created_at: string;
  updated_at: string;
  title: string;
  status: 'active' | 'archived';
  message_count: number;
  messages: InvestigationMessage[];
}

export interface SearchPeriodConfig {
  period_type: 'none' | 'day' | 'week' | 'month' | 'year';
}

// 新しい統合された調査アイテム型
export interface InvestigationItem {
  id: string;
  userId: string;

  // 基本情報（元InvestigationTarget）
  project_id: string;
  project_title: string;
  fact_index: number;
  claim: string;
  verification_result: string;
  verification_summary: string; // source.summaryから抽出
  confidence: number;
  added_at: string;

  // 状態管理
  status: 'waiting' | 'investigating';
  investigation_started_at?: string;
  updated_at: string;

  // 調査データ（status = investigating のみ）
  title?: string;
  messages?: InvestigationMessage[];
  message_count?: number;
}

// 追加調査API関連の型
export interface InvestigationMessageRequest {
  session_id: string;
  message: string;
  agent_type:
    | 'auto'
    | 'general_conversation'
    | 'perplexity_search'
    | 'strategy_planning'
    | 'gemini_grounding'
    | 'fact_verification';
  mode: 'fact_based' | 'free_chat';
  target_id?: string;
  selected_target?: InvestigationTarget | any;
  search_config?: SearchPeriodConfig;
  conversation_history?: any[];
  user_id?: string; // 後方互換性のため残す
}

export interface InvestigationMessageResponse {
  message_id: string;
  agent_response: string;
  agent_type: string;
  metadata?: Record<string, any>;
  timestamp: string;
}

export interface CrossSearchRequest {
  session_id: string;
  query: string;
  period_config: SearchPeriodConfig;
  fact_context?: InvestigationTarget;
}

export interface StrategyPlanningRequest {
  session_id: string;
  topic: string;
  context?: InvestigationTarget;
}

// ===== グループ管理関連の型定義 =====

export interface Group {
  id: string;
  name: string;
  description?: string;
  color: string; // HEXカラー
  ownerId: string;
  createdAt: string;
  updatedAt: string;
}

export interface GroupMember {
  id: string; // userIdと同じ
  userId: string;
  role: 'owner' | 'member';
  joinedAt: string;
  displayName: string;
  photoURL?: string;
}

export interface GroupInvite {
  id: string; // 招待トークン
  createdBy: string;
  expiresAt: string;
  usedBy?: string;
  usedAt?: string;
}

export interface GroupContextType {
  groups: Group[];
  currentGroup: Group | null;
  loading: boolean;
  setCurrentGroup: (group: Group | null) => void;
  refreshGroups: () => Promise<void>;
}

// ===== 案件関連の型定義 =====

export interface Workspace {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'archived';
  groupIds: string[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// ファクト参照（チャットメッセージに添付されるファクト情報）
export interface FactReference {
  itemId: string; // FactCheckItem ID
  factIndex: number; // facts配列のインデックス
  itemTitle: string; // 表示用（非正規化）
  claim: string; // ファクトの主張
  result?: string; // 検証結果（completed時）
  startSec?: number; // 動画ファクトの場合のタイムコード
}

export interface WorkspaceMessage {
  id: string;
  threadId?: string;
  itemId?: string;
  authorId: string;
  content: string;
  mentions?: string[];
  factReferences?: FactReference[]; // 添付されたファクト参照
  createdAt: string;
  updatedAt?: string;
}

export interface WorkspaceContextType {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  loading: boolean;
  filter: { groupId: string | null };
  setFilter: (filter: { groupId: string | null }) => void;
  setCurrentWorkspace: (workspace: Workspace | null) => void;
  refreshWorkspaces: () => Promise<void>;
}

// ===== ファクトチェックアイテム関連の型定義 =====

export type FactCheckItemType = 'document' | 'video_url' | 'video_upload' | 'image' | 'fact';
export type FactCheckItemStatus = 'extracting' | 'extracted' | 'verifying' | 'completed' | 'error';

// 動画処理ステージ（動画アップロードのみで使用）
export type VideoProcessingStage =
  | 'uploading' // アップロード中
  | 'upload_interrupted' // アップロード中断
  | 'uploaded' // アップロード完了、ASR待機中
  | 'transcribing' // ASR処理中
  | 'transcribed' // ASR完了
  | null; // 動画処理完了（以降はstatusのみで管理）

// アップロード再開用情報
export interface UploadResumable {
  uploadUri: string; // GCS Resumable Upload URI
  totalBytes: number; // 総バイト数
  uploadedBytes: number; // アップロード済みバイト数
  fileName: string; // 元ファイル名（照合用）
  fileSize: number; // 元ファイルサイズ（照合用）
  lastModified: number; // 元ファイルの最終更新日時（照合用）
  startedAt: string; // 開始日時
}

/** 1つの案件に、どの結果を見せているか */
export interface ItemShare {
  factCheck: boolean;
  compliance: boolean;
}

export interface FactCheckItem {
  id: string;
  /** @deprecated 旧「どの案件に属するか」。共有は shares / sharedWorkspaceIds で持つ。
   *  移行の戻し道として残してあるだけで、表示や絞り込みには使わない。 */
  workspaceId: string | null;
  /** 絞り込み用。この案件に何かを見せている、という一覧 */
  sharedWorkspaceIds?: string[];
  /** 案件ごとに、何を見せているか */
  shares?: Record<string, ItemShare>;
  type: FactCheckItemType;
  title: string;
  description?: string;
  /** アップロード時に入力した資料メタ情報 */
  sourceMeta?: {
    docType: string;
    title: string;
    asOf: string;
    summary: string;
  };

  // 担当・ステータス
  assigneeId?: string;
  status: FactCheckItemStatus;

  // ファイル・ソース情報
  sourceUrl?: string;
  fileUrl?: string;
  fileName?: string;
  blobName?: string;

  // 動画ファイル情報（video_upload用）
  durationSec?: number; // 動画の長さ（秒）
  mimeType?: string; // video/mp4 等
  sizeBytes?: number; // バイト数

  // 文字起こし（ASR）メタデータ（video_upload用）
  transcriptBlobName?: string; // GCS上の文字起こしJSONLのブロブ名
  transcriptLanguage?: string; // ja など
  asrProvider?: string; // openai_transcribe
  asrModel?: string; // gpt-4o-transcribe

  // 動画処理ステージ（video_upload用）
  processingStage?: VideoProcessingStage;

  // 映像解析（テロップ・ロゴ・情景のメタ化）用（video_upload用）
  videoMode?: VideoMode; // テロップ有無（アップロード時にユーザー指定）
  analysisStatus?: VideoAnalysisStatus; // 既存の文字起こしFCとは独立したステータス
  analysisProgress?: number; // 0-100
  // CM を含む録画（アップロード時にユーザー指定）。指定時は CM 区間を先に検出して解析対象から外す
  hasCommercials?: boolean;
  cmDetectStatus?: 'pending' | 'detecting' | 'done' | 'failed';
  cmRangeCount?: number;
  cmTotalSec?: number;
  cmRanges?: CmRange[];

  // 放送規程チェック（横断・ユーザー操作で実行）
  complianceStatus?: ComplianceStatus;
  complianceProgress?: number; // 0-100
  complianceFindingCount?: number;
  /** 一覧で内訳を出すための件数。古いデータには無い（その場合は総数だけ出す） */
  complianceBadCount?: number;
  complianceCautionCount?: number;
  complianceUnjudgedCount?: number;
  complianceDroppedCount?: number;
  complianceCheckedAt?: string; // ISO8601

  // アップロード再開用情報（video_upload用）
  uploadResumable?: UploadResumable;

  // ファクトチェックデータ（旧FactCheckProjectから継承）
  facts: ExtractedFact[];
  selectedFactIndices?: number[];
  verification_results: VerificationResult[];
  total_facts: number;
  verified_facts: number;

  // メタデータ
  createdBy: string;
  createdAt: string;
  updatedAt: string;

  // type: 'fact' 用フィールド（ドメイン限定ファクトチェックからの共有）
  claim?: string; // ファクトの主張
  verification_result?: string; // 判定結果（正確/不正確/部分的に正確/検証不可能）
  source?: {
    // 検証詳細
    summary: string;
    reason: string;
    sources: DomainVerificationSource[];
    thought_process?: string; // 検証の思考プロセス
  };
}

// ===== 放送規程チェック（横断・走査→検証）の型定義 =====

export type ComplianceStatus = 'pending' | 'checking' | 'completed' | 'error';

// 抵触度: bad=×(抵触の可能性) / caution=△(要確認)
export type ComplianceRating = 'bad' | 'caution';

/**
 * 懸念の「広さ」。走査が付けて GCS まで保存されるが、**画面では読んでいない**（2026-10-19 時点）。
 * - unit    その表現そのもの（言い換えで直る）
 * - segment その区間の流れ・積み重ね（構成の見直しが要る）
 * - whole   番組全体として
 * 出していない理由と、出すときの実装箇所は #146 に書いてある。
 * whole は locator の「全体」と source_type で既に画面に出ているので、実質 segment 用。
 */
export type ComplianceScope = 'unit' | 'segment' | 'whole';

export interface ComplianceLocator {
  start_sec?: number; // 動画
  end_sec?: number;
  page?: number; // ドキュメント
  section?: string;
  raw?: string; // 解釈不能時（例「全体」）
}

export interface ComplianceFinding {
  id: string;
  scope: ComplianceScope; // 保存のみ・画面未使用（#146）
  source_type?: string; // telop/transcript/scene/logo/fact/document/whole
  locator: ComplianceLocator;
  expression: string; // 抵触しそうな表現
  category?: string;
  rating: ComplianceRating;
  // "unjudged": 自動判定できなかったもの（検索上限で条文を特定できず等）。条文は付かない
  judge_status?: 'unjudged';
  rule_id?: string | null;
  rule_chapter?: string | null; // 条文の章（例「第7章 光感受性発作への配慮」）
  rule_article_no?: string | null;
  rule_title?: string | null; // 条文の見出し
  rule_excerpt?: string; // 該当条文（引用）
  scan_reason?: string; // 走査がこの表現を拾った理由（AI指摘の理由）
  explanation?: string;
  suggestion?: string;
  citations?: string[]; // 出典（規程ファイル名 等）
  source_url?: string; // ハンドブックURL（CSV突合で付与・任意）
  source_key?: 'minpo' | 'handbook'; // 出典PDF（/api/rules/<key>.pdf・ログイン必須）
  source_label?: string; // 出典表示名（民放連 放送基準 / 放送ハンドブック2023）
  source_page?: number; // 該当ページ（1始まり）
}

/** 走査は拾ったが、該当条文が無く不採用になった懸念（判定も条文も持たない） */
export interface ComplianceDroppedConcern {
  id: string;
  scope: ComplianceScope; // 保存のみ・画面未使用（#146）
  source_type?: string;
  locator: ComplianceLocator;
  expression: string;
  category?: string;
  scan_reason?: string;
}

export interface ComplianceResult {
  cmDetectStatus?: 'pending' | 'detecting' | 'done' | 'failed';
  cmRanges?: CmRange[]; // 解析対象から外した CM 区間
  cmTotalSec?: number;
  item_id: string;
  complianceStatus?: ComplianceStatus;
  complianceProgress?: number;
  complianceFindingCount?: number;
  concern_count?: number;
  complianceCheckedAt?: string; // ISO8601（チェック完了時刻）
  findings?: ComplianceFinding[];
  dropped_concerns?: ComplianceDroppedConcern[];
}

// ===== 映像解析（テロップ・ロゴ・情景のメタ化）の型定義 =====

export type VideoMode = 'with_telop' | 'offline';

/** 検出された CM 区間（秒・元動画のタイムコード） */
export interface CmRange {
  start_sec: number;
  end_sec: number;
  label?: string;
  uncertain?: boolean;
}

export type VideoAnalysisStatus =
  | 'pending'
  | 'preprocessing'
  | 'waiting_transcript'
  | 'analyzing'
  | 'aggregating'
  | 'completed'
  | 'error'
  | 'skipped_unsupported';

// 段2 テロップ妥当性判定（○△×）
export type TelopRating = 'ok' | 'caution' | 'bad';

export interface TelopCheck {
  rating?: TelopRating; // ok=○ / caution=△ / bad=×
  reason?: string; // △/× の根拠
  suspected_typo?: { text?: string; suggestion?: string }; // 誤字・変換ミスの疑い（断定しない）
}

// テロップ span（段1.5集約後）
export interface TelopSpan {
  text: string;
  type?: string;
  position?: string;
  start_sec: number;
  end_sec: number;
  coverage?: number;
  persistence?: 'persistent' | 'transient';
  // 常設のみ: 段0（program_map）由来の種別と画面位置
  kind?: 'program' | 'segment' | 'unknown';
  screen_position?: string;
  // 常設のみ: 途中で消えて再び出た場合の各区間（start/end は最初〜最後の範囲）
  occurrences?: { start_sec: number; end_sec: number }[];
  frame_count?: number;
  variants?: string[];
  check?: TelopCheck; // transient_findings のみ
}

// フレーム単位観測（情景タイムライン用）
export interface FrameObservation {
  timecode_sec: number;
  timecode: string;
  covers_until_sec?: number;
  telops?: { text: string; type?: string; position?: string; emphasis?: boolean }[];
  logos_brands?: { text?: string; kind?: string; position?: string }[];
  scene?: {
    scene_type?: string;
    setting?: string;
    description?: string;
    is_cut_boundary?: boolean;
  };
  entities?: {
    people?: { label?: string; role_guess?: string; expression?: string; action?: string }[];
    objects?: string[];
    locations?: string[];
    graphics?: string[];
  };
  content_signals?: { privacy?: string[]; caution?: string[] };
}

export interface VideoAnalysisResult {
  item_id: string;
  analysisStatus?: VideoAnalysisStatus;
  analysisProgress?: number;
  analysisError?: string;
  videoMode?: VideoMode;
  cmDetectStatus?: 'pending' | 'detecting' | 'done' | 'failed';
  cmRanges?: CmRange[]; // 解析対象から外した CM 区間
  cmTotalSec?: number;
  telop_analysis?: {
    telop_spans?: TelopSpan[];
    persistent_overlays?: TelopSpan[];
    transient_findings?: TelopSpan[];
    screen_texts?: TelopSpan[]; // スタジオ表示(モニター/フリップ・判定対象外)
  } | null;
  metadata?: {
    frame_count?: number;
    frames?: FrameObservation[];
    video?: { width?: number; height?: number; duration_sec?: number };
  } | null;
}

// ===== 通知関連の型定義 =====

export type NotificationType = 'mention' | 'invite' | 'assignment' | 'item_completed';

export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  body: string;
  link?: string;
  read: boolean;
  createdAt: string;
}

// ===== 動画アップロード関連の型定義 =====

// 動画アップロード初期化APIのレスポンス
export interface VideoUploadInitResponse {
  itemId: string;
  uploadUri: string; // GCS Resumable Upload URI
  blobName: string; // GCSのブロブ名
}

// 動画アップロード完了APIのレスポンス
export interface VideoUploadCompleteResponse {
  success: boolean;
  fileUrl: string; // 署名付きURL
  durationSec?: number; // 動画の長さ（秒）
}

// ===== 文字起こし補正関連の型定義 =====

// 補正済み文字起こしセグメント
export interface RefinedSegment {
  segment_id: string;
  start_sec: number;
  end_sec: number;
  original_text: string;
  refined_text: string;
}

// 補正済み文字起こし全体
export interface RefinedTranscript {
  item_id: string;
  segments: RefinedSegment[];
  total_duration_sec: number;
  created_at: string;
}

// ステータスバッジの表示情報
export interface StatusBadgeInfo {
  label: string;
  color: 'blue' | 'green' | 'yellow' | 'red' | 'gray' | 'orange';
}

// ステータスバッジ取得ヘルパー関数
export function getStatusBadge(item: FactCheckItem): StatusBadgeInfo {
  // 動画アップロード中のステータス（動画特有）
  if (item.processingStage) {
    switch (item.processingStage) {
      case 'uploading':
        return { label: 'アップロード中', color: 'orange' };
      case 'upload_interrupted':
        return { label: 'アップロード中断', color: 'yellow' };
      case 'uploaded':
        return { label: '処理待ち', color: 'orange' };
      case 'transcribing':
        return { label: '処理中', color: 'orange' };
      case 'transcribed':
        return { label: '抽出中', color: 'orange' };
    }
  }

  // 共通ステータス（ドキュメント・動画共通）
  switch (item.status) {
    case 'extracting':
      return { label: '抽出中', color: 'orange' };
    case 'extracted':
      return { label: '抽出レビュー', color: 'blue' };
    case 'verifying':
      return { label: '検証中', color: 'orange' };
    case 'completed':
      return { label: '検証完了', color: 'green' };
    case 'error':
      return { label: 'エラー', color: 'red' };
    default:
      return { label: '不明', color: 'gray' };
  }
}

// ===== ドメイン制限ファクトチェック関連の型定義 =====

export interface DomainList {
  id: string;
  name: string;
  description: string;
  domains: string[];
  is_system: boolean; // システムテンプレートか
  created_by: string; // ユーザーID or "system"
  created_at: string;
  updated_at: string;
}

export interface QueryGenerationResponse {
  claim: string; // 抽出された主張
  suggested_queries: string[]; // 検索クエリ案3つ
}

export interface DomainVerificationSource {
  title: string;
  url: string;
}

export interface DomainVerificationResult {
  judgement: string; // 正確/不正確/部分的に正確/検証不可能
  summary: string; // 検証結果の要約（200文字以内）
  reason: string; // 詳細な判定理由
  sources: DomainVerificationSource[]; // 参照したソース
  thought_process: string; // 検証過程の思考プロセス
  tavily_answer?: string; // Tavilyの参考回答
  source_count: number; // 確認に使用したソース数
}

// Tavily検索オプション
export type TavilyTimeRange = 'day' | 'week' | 'month' | 'year' | null;

export interface TavilySearchOptions {
  time_range: TavilyTimeRange;
}

// ドメイン限定ファクトチェック履歴
export interface DomainFactCheckHistoryItem {
  id: string;
  user_id: string;
  claim: string;
  query: string;
  user_input: string; // 元のユーザー入力
  domain_list_ids: string[];
  time_range: TavilyTimeRange;
  result: DomainVerificationResult;
  created_at: string;
}
